import { BulkUploadModal, ValidationError, BulkUploadResult, LanguageColumnMapping } from '@/components/common/BulkUploadModal';
import { Employee } from '@/types/resources/employee';
import { useAuth } from '@/contexts/AuthContext';
import { addEmployeesBulk } from '@/api/employees';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTemplateURL } from '@/templates/csv/templateLoader';
import { Modal, Progress, Button, Row, Col, Typography, Tag } from 'antd';
import { UserOutlined, TeamOutlined, CalendarOutlined } from '@ant-design/icons';
import { useState } from 'react';

const { Title, Text } = Typography;

interface EmployeesBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export function EmployeesBulkUploadModal({ visible, onCancel, onSuccess }: EmployeesBulkUploadModalProps) {
  const { firestoreUser, refreshWorkspaceStats } = useAuth();
  const { t, i18n } = useTranslation(['employees', 'common']);
  const currentLanguage = i18n.language as 'en' | 'es' | 'pt';
  
  // State for managing the flow
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'creating'>('upload');
  const [processedData, setProcessedData] = useState<Array<{
    employee: Omit<Employee, 'id' | 'workspace' | 'activityRecords'>;
    displayName: string;
    email: string;
    phoneNumber: string;
    employeeId: string;
    department: string;
    role: string;
    status: string;
    joiningDate: string;
  }>>([]);

  // Sample data for template generation
  const sampleData: any[] = [];

  // Column definitions for the template (internationalized)
  const columns = [
    { key: 'displayName', title: t('employees:fields.displayName'), required: true },
    { key: 'email', title: t('employees:fields.email'), required: true },
    { key: 'phoneNumber', title: t('employees:fields.phoneNumber'), required: true },
    { key: 'employeeId', title: t('employees:fields.employeeId'), required: false },
    { key: 'department', title: t('employees:fields.department'), required: false },
    { key: 'role', title: t('employees:fields.role'), required: false },
    { key: 'status', title: t('employees:fields.status'), required: false },
    { key: 'joiningDate', title: t('employees:fields.joiningDate'), required: false },
    { key: 'notes', title: t('employees:fields.notes'), required: false }
  ];

  // Language-aware column mappings for CSV headers
  const columnMappings: LanguageColumnMapping = {
    en: {
      displayName: ['displayname', 'display name', 'name', 'full name', 'fullname', 'employee name'],
      email: ['email', 'email address', 'emailaddress', 'e-mail'],
      phoneNumber: ['phonenumber', 'phone number', 'phone', 'mobile', 'contact number', 'contactnumber'],
      employeeId: ['employeeid', 'employee id', 'employee_id', 'emp id', 'empid', 'staff id', 'staffid'],
      department: ['department', 'dept', 'division', 'unit'],
      role: ['role', 'position', 'job title', 'jobtitle', 'designation'],
      status: ['status', 'employee status', 'employeestatus'],
      joiningDate: ['joiningdate', 'joining date', 'joining_date', 'start date', 'startdate', 'hire date', 'hiredate'],
      notes: ['notes', 'comments', 'description', 'remarks']
    },
    es: {
      displayName: ['nombredisplay', 'nombre display', 'nombre', 'nombre completo', 'nombrecompleto', 'nombre del empleado'],
      email: ['email', 'correo electrónico', 'correo', 'e-mail'],
      phoneNumber: ['numerotelefono', 'número de teléfono', 'teléfono', 'móvil', 'número de contacto', 'numerocontacto'],
      employeeId: ['idempleado', 'id empleado', 'id_empleado', 'id emp', 'idemp', 'id personal', 'idpersonal'],
      department: ['departamento', 'depto', 'división', 'unidad'],
      role: ['rol', 'cargo', 'título del trabajo', 'titulotrabajo', 'designación'],
      status: ['estado', 'estado del empleado', 'estadodelempleado'],
      joiningDate: ['fechaincorporacion', 'fecha de incorporación', 'fecha_incorporacion', 'fecha inicio', 'fechainicio', 'fecha contratación', 'fechacontratacion'],
      notes: ['notas', 'comentarios', 'descripción', 'observaciones']
    },
    pt: {
      displayName: ['nomedisplay', 'nome display', 'nome', 'nome completo', 'nomecompleto', 'nome do funcionário'],
      email: ['email', 'correio eletrônico', 'correio', 'e-mail'],
      phoneNumber: ['numerotelefone', 'número de telefone', 'telefone', 'móvel', 'número de contato', 'numerocontato'],
      employeeId: ['idfuncionario', 'id funcionário', 'id_funcionario', 'id func', 'idfunc', 'id pessoal', 'idpessoal'],
      department: ['departamento', 'depto', 'divisão', 'unidade'],
      role: ['função', 'cargo', 'título do trabalho', 'titulotrabalho', 'designação'],
      status: ['status', 'status do funcionário', 'statusdofuncionario'],
      joiningDate: ['dataincorporacao', 'data de incorporação', 'data_incorporacao', 'data inicio', 'datainicio', 'data contratação', 'datacontratacao'],
      notes: ['notas', 'comentários', 'descrição', 'observações']
    }
  };

  // Validation function for employee records
  const validateEmployeeRecord = (record: any, rowIndex: number): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Required field validation
    if (!record.displayName || record.displayName.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'displayName',
        value: record.displayName,
        message: t('employees:bulkUpload.validation.displayNameRequired')
      });
    } else if (record.displayName.length > 100) {
      errors.push({
        row: rowIndex,
        field: 'displayName',
        value: record.displayName,
        message: t('employees:bulkUpload.validation.displayNameTooLong')
      });
    }

    if (!record.email || record.email.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'email',
        value: record.email,
        message: t('employees:bulkUpload.validation.emailRequired')
      });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(record.email)) {
      errors.push({
        row: rowIndex,
        field: 'email',
        value: record.email,
        message: t('employees:bulkUpload.validation.emailInvalid')
      });
    }

    if (!record.phoneNumber || record.phoneNumber.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'phoneNumber',
        value: record.phoneNumber,
        message: t('employees:bulkUpload.validation.phoneNumberRequired')
      });
    }

    // Optional field validation - only validate format if provided
    if (record.employeeId && record.employeeId.trim() !== '') {
      // Add any format validation for employeeId if needed
    }

    if (record.department && record.department.trim() !== '') {
      // Add any format validation for department if needed
    }

    if (record.role && record.role.trim() !== '') {
      // Add any format validation for role if needed
    }

    if (record.status && record.status.trim() !== '') {
      if (!['active', 'inactive', 'on_leave', 'terminated'].includes(record.status.toLowerCase())) {
        errors.push({
          row: rowIndex,
          field: 'status',
          value: record.status,
          message: t('employees:bulkUpload.validation.statusInvalid')
        });
      }
    }

    if (record.joiningDate && record.joiningDate.trim() !== '') {
      const joiningDate = new Date(record.joiningDate);
      if (isNaN(joiningDate.getTime())) {
        errors.push({
          row: rowIndex,
          field: 'joiningDate',
          value: record.joiningDate,
          message: t('employees:bulkUpload.validation.joiningDateInvalid')
        });
      }
    }

    return errors;
  };

  // Transform CSV record to Employee type
  const transformEmployeeRecord = (record: any): Omit<Employee, 'id' | 'workspace' | 'activityRecords'> => {
    return {
      employeeId: record.employeeId?.trim() || '',
      role: record.role?.toLowerCase() as any || 'employee',
      status: record.status?.toLowerCase() as any || 'active',
      department: record.department?.trim() || '',
      shiftTiming: [], // Default empty array
      joiningDate: record.joiningDate ? new Date(record.joiningDate) : new Date(),
      user: {
        id: '', // Will be generated by the backend
        displayName: record.displayName.trim(),
        email: record.email.trim(),
        phoneNumber: record.phoneNumber.trim()
      },
      contacts: [],
      notes: record.notes || ''
    };
  };

  // Handle bulk upload confirmation - shows preview first
  const handleBulkUploadConfirm = async (result: BulkUploadResult<Omit<Employee, 'id' | 'workspace' | 'activityRecords'>>) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      // Process the data to show preview
      const processedData = result.validRecords.map(employeeData => ({
        employee: employeeData,
        displayName: employeeData.user?.displayName || '',
        email: employeeData.user?.email || '',
        phoneNumber: employeeData.user?.phoneNumber || '',
        employeeId: employeeData.employeeId || '',
        department: employeeData.department || '',
        role: employeeData.role || 'employee',
        status: employeeData.status || 'active',
        joiningDate: employeeData.joiningDate ? employeeData.joiningDate.toLocaleDateString() : ''
      }));

      setProcessedData(processedData);
      setCurrentStep('preview');

    } catch (error) {
      console.error('Error processing data:', error);
      message.error('Error processing data. Please try again.');
    }
  };

  // Handle the actual creation after confirmation
  const handleConfirmCreation = async () => {
    if (!firestoreUser || processedData.length === 0) {
      message.error('No data to process');
      return;
    }

    try {
      setCurrentStep('creating');
      
      let totalUploaded = 0;
      const totalRecords = processedData.length;
      
      // Show progress modal
      const progressModal = Modal.info({
        title: 'Creating Employees',
        content: (
          <div>
            <Progress 
              percent={0} 
              status="active" 
              format={(percent) => `${totalUploaded}/${totalRecords} (${percent}%)`}
            />
            <p>Creating employees...</p>
          </div>
        ),
        okButtonProps: { style: { display: 'none' } }
      });

      // Prepare employees data for bulk upload
      const employeesData = processedData.map(data => data.employee);

      // Create employees in bulk
      const result = await addEmployeesBulk(employeesData, firestoreUser, (uploaded, total) => {
        const percent = Math.round((uploaded / total) * 100);
        progressModal.update({
          content: (
            <div>
              <Progress 
                percent={percent} 
                status="active" 
                format={(percent) => `${uploaded}/${total} (${percent}%)`}
              />
              <p>Creating employees: {uploaded} of {total}</p>
            </div>
          )
        });
      });

      totalUploaded = result.successCount;

      // Refresh workspace stats
      try {
        await refreshWorkspaceStats();
      } catch (error) {
        console.warn('Failed to refresh workspace stats:', error);
      }

      progressModal.destroy();
      message.success(`Successfully created ${totalUploaded} employees`);
      
      onSuccess();
    } catch (error) {
      console.error('Progress-aware upload error:', error);
      message.error('Error creating employees. Please try again.');
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setCurrentStep('upload');
    setProcessedData([]);
    onCancel();
  };

  // Render different content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <BulkUploadModal
            visible={visible}
            onCancel={handleModalCancel}
            onConfirm={handleBulkUploadConfirm}
            title={t('employees:actions.bulkUpload')}
            templateUrl={getTemplateURL(currentLanguage, 'employees')}
            templateFileName={`employees_template_${currentLanguage}.csv`}
            validateRecord={validateEmployeeRecord}
            transformRecord={transformEmployeeRecord}
            columns={columns}
            sampleData={sampleData}
            accept=".csv"
            maxFileSize={5}
            language={currentLanguage}
            columnMappings={columnMappings}
          />
        );

      case 'preview':
        return (
          <Modal
            title={t('employees:bulkUpload.title')}
            open={visible}
            onCancel={handleModalCancel}
            footer={[
              <Button key="cancel" onClick={handleModalCancel}>
                {t('common:actions.cancel')}
              </Button>,
              <Button key="confirm" type="primary" onClick={handleConfirmCreation}>
                {t('common:actions.confirm')}
              </Button>
            ]}
            width={800}
          >
            <div style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={3} style={{ color: '#1890ff', marginBottom: '8px' }}>
                  {t('employees:bulkUpload.preview.title')}
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  {t('employees:bulkUpload.preview.subtitle')}
                </Text>
              </div>

              {/* Summary Details */}
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '20px', 
                borderRadius: '6px', 
                border: '1px solid #d9d9d9',
                marginBottom: '24px'
              }}>
                {processedData.map((data, index) => (
                  <div key={index} style={{ 
                    marginBottom: index < processedData.length - 1 ? '20px' : '0',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: '#1890ff', 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: '16px'
                      }}>
                        <UserOutlined style={{ color: '#fff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                          {data.displayName}
                        </Title>
                        <Text style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                          {data.employeeId} • {data.email}
                        </Text>
                      </div>
                    </div>

                    <Row gutter={16}>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                          <Tag color="green" style={{ margin: 0 }}>
                            {data.status}
                          </Tag>
                          <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }}>
                            Status
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                          <TeamOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                          <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px' }}>
                            {data.department}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                          <Tag color="orange" style={{ margin: 0 }}>
                            {data.role}
                          </Tag>
                          <div style={{ fontSize: '12px', color: '#fa8c16', marginTop: '4px' }}>
                            Role
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                          <CalendarOutlined style={{ fontSize: '16px', color: '#666' }} />
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {data.joiningDate}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}

                {/* Confirmation Message */}
                <div style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  backgroundColor: '#fff2e8', 
                  borderRadius: '6px', 
                  border: '1px solid #ffbb96',
                  textAlign: 'center'
                }}>
                  <Text style={{ margin: 0, color: '#d46b08', fontSize: '14px' }}>
                    {t('employees:bulkUpload.preview.confirmationMessage', {
                      count: processedData.length
                    })}
                  </Text>
                </div>
              </div>
            </div>
          </Modal>
        );

      case 'creating':
        return (
          <Modal
            title="Creating Employees..."
            open={visible}
            onCancel={handleModalCancel}
            footer={null}
            width={600}
          >
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Progress type="circle" percent={100} status="active" />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                Please wait while the employees are being created...
              </Text>
            </div>
          </Modal>
        );

      default:
        return null;
    }
  };

  return renderContent();
}
